import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { TextField } from '@mui/material'
import { Controller } from 'react-hook-form'
import type { Control as ControlProps } from 'react-hook-form'
import type { Theme } from '@mui/material/styles'
import { alpha } from '@mui/material/styles'
import { makeStyles } from 'tss-react/mui'
import { StackItem } from 'ui/layout/StackItem'
import type { StackItemProps } from 'ui/layout/StackItem'

export const useStyles = makeStyles()((theme: Theme) => ({
  required: {
    backgroundColor: alpha(theme.palette.secondary.main, 0.04),
  },
  firstInput: {
    paddingRight: theme.spacing(2),
  },
}))

export type StackItemTextFieldProps = {
  /**
   *  ReactHookFormで使うname
   */
  name: string
  /**
   * placeholder
   */
  placeholder?: string
  /**
   * エラーがある時はエラーメッセージ
   */
  errorMessage?: React.ReactNode
  /**
  /**
   * フォームに最初に入っている値
   */
  defaultValue?: string
  /**
   * ReactHookFormに必須のオブジェクト
   */
  control: ControlProps
  /**
   * TextFormの幅を100%にする
   */
  fullWidth?: boolean
  /**
   * TextAreaにするかどうか
   */
  multiline?: boolean
  /**
   * TextAreaにする時の行数
   */
  rows?: number
  /**
   * Controlにセットするイベントハンドラ
   */
  onChange?: (obj: any) => void
  /**
   * 文字数を表示するときの最大数
   */
  textCounter?: number
  /**
   * フォームの最小幅
   */
  inputMinWidth?: number
  /**
   * data-testIdの値（デフォルトでname設定済み）
   */
  testId?: string
} & StackItemProps

/**
 * StackItem + TextField + ReactHookForm 通常使用のショートハンド
 */
export const StackItemTextField: React.FC<StackItemTextFieldProps> = ({
  errorMessage,
  placeholder,
  control,
  defaultValue,
  name,
  required,
  fullWidth,
  multiline,
  rows,
  onChange,
  textCounter,
  inputMinWidth,
  testId,
  ...stackItemProps
}: StackItemTextFieldProps) => {
  const { t } = useTranslation()
  const { classes, cx } = useStyles()
  const [counterText, setCounterText] = useState<string>('')

  const handleCount = (value: string) => {
    setCounterText(
      `${value.length}/${textCounter}${t('文字', { count: value.length })}`
    )
  }

  return (
    <StackItem {...{ ...stackItemProps, required }}>
      <Controller
        name={name}
        control={control}
        defaultValue={defaultValue}
        render={({ field }) => (
          <TextField
            {...field}
            style={
              inputMinWidth ? { minWidth: `${inputMinWidth}px` } : undefined
            }
            inputProps={{
              'data-testid': testId || name,
            }}
            InputProps={{
              className: cx(!field?.value && required && classes.required),
            }}
            variant="outlined"
            placeholder={placeholder}
            error={!!errorMessage}
            helperText={[counterText, errorMessage].join('')}
            fullWidth={fullWidth}
            multiline={multiline}
            rows={multiline ? rows ?? 4 : 1}
            onChange={(event) => {
              onChange && onChange(event.target.value)
              textCounter && handleCount(event.target.value)
              field?.onChange(event)
            }}
          />
        )}
      />
    </StackItem>
  )
}
